import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Avatar,
	BookmarkOutline,
	Box,
	Center,
	ChatEmptyOutline,
	DotsHorizontalOutline,
	HStack,
	Image,
	rem,
	Spacer,
	Stack,
	Text,
	ThumbUpOutline,
} from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import medalImage from '../../../assets/images/medal.png';
import { TextPreview } from '../../../components/bonus/text-preview';
import { ActionButton } from '../../../components/recognition/bonus/components/bonus/action-button';

type FeedPreviewProps = {
	amount: number;
	iconURL: string | null;
	message: string;
	name: string;
};

export const FeedPreview: FC<FeedPreviewProps> = ({ amount, iconURL, message, name }) => {
	const { t } = useTranslation('incentives', { keyPrefix: 'form' });
	const user = useCurrentUser();

	const updatedMessage = `+${amount || 0} @${user.username} ${message}`;

	return (
		<Box bgColor="bg.surface" borderRadius="lg" border="1px solid" borderColor="border.default">
			{/* Header */}
			<HStack p={4} columnGap={2}>
				{iconURL && <Image src={iconURL} boxSize={rem(38)} borderRadius="full" objectFit="cover" />}
				{!iconURL && <Image src={medalImage} boxSize={rem(38)} borderRadius="full" objectFit="cover" />}
				<Center bgColor="points.bgAlpha" h={rem(38)} borderRadius="full" color="points.fg" fontWeight="semibold" px={2.5} fontSize="lg">
					+{amount ? Math.trunc(Math.abs(amount)) : 0}
				</Center>
				<Spacer />
				<Text size="sm" color="text.soft">
					{t('nowTimestamp')}
				</Text>
				<BookmarkOutline color="secondary.fg" />
				<DotsHorizontalOutline color="secondary.fg" />
			</HStack>

			{/* Body */}
			<Stack py={0} px={4}>
				<HStack w="full">
					<Text mb={2} fontSize={500} fontWeight="bold" fontFamily="display">
						{name.trim() || t('previewDefaultTitle')}
					</Text>
					<Spacer />
					<Avatar src={user.profile_pic_url} name={user.display_name} size="lg" />
				</HStack>
				<TextPreview text={updatedMessage}>
					<Text as="span" fontWeight="bold">
						{name.trim() || t('previewDefaultTitle')}:{' '}
					</Text>
				</TextPreview>
			</Stack>

			{/* Footer */}
			<HStack px={5} py={4} spacing={2}>
				<ActionButton icon={<ChatEmptyOutline />} color="gray">
					{t('comment')}
				</ActionButton>

				<Spacer />

				<ActionButton icon={<ThumbUpOutline />} color="blue">
					{t('like')}
				</ActionButton>
			</HStack>
		</Box>
	);
};
